// Gestão DNA · Home — atalhos, módulos liberados e destaques do dia (zero financeiro).
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Cake, CalendarDays, FileSignature, LifeBuoy, Loader2, Search, UserPlus,
  Users, ClipboardList, Printer, TriangleAlert, ArrowUpRight, GraduationCap,
} from 'lucide-react';
import api from '@/lib/api';
import { getUser } from '@/lib/auth';
import { TONES } from './registry';
import { C, KpiCard, PageHeader } from '@/design-system/dna';

const TONE_COLOR = { violet: C.purple, rose: C.magenta, sky: C.green, amber: C.amber };

const fade = (i = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3, delay: 0.04 * i, ease: 'easeOut' },
});

const ATALHOS = [
  { key: 'buscar', label: 'Buscar aluno', icon: Search, to: '/gestao/alunos' },
  { key: 'novo-aluno', label: 'Novo aluno', icon: UserPlus, to: '/gestao/alunos?novo=1' },
  { key: 'novo-resp', label: 'Novo responsável', icon: Users, to: '/gestao/responsaveis?novo=1' },
  { key: 'notas', label: 'Consultar notas', icon: ClipboardList, to: '/gestao/notas' },
  { key: 'recuperacao', label: 'Recuperação', icon: LifeBuoy, to: '/gestao/recuperacao' },
  { key: 'documento', label: 'Imprimir documento', icon: Printer, to: '/gestao/documentos' },
];

function Metric({ icon, label, value, hint, tone = 'violet', i }) {
  return <KpiCard id={`gestao-${label.toLowerCase().replace(/\s+/g, '-')}`} icon={icon} label={label} value={value} hint={hint} color={TONE_COLOR[tone] || C.purple} index={i} />;
}

function Painel({ title, icon: Icon, children, empty, items, action, testId }) {
  return (
    <motion.section {...fade(1)}
      className="rounded-[24px] border bg-white p-6" style={{ borderColor: C.line }}
      data-testid={testId}>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Icon className="h-4 w-4" style={{ color: C.purple }} />
          <h3 className="text-base font-extrabold tracking-tight" style={{ color: C.ink }}>{title}</h3>
        </div>
        {action}
      </div>
      {(!items || items.length === 0)
        ? <p className="py-6 text-center text-sm text-slate-400">{empty}</p>
        : <div className="divide-y divide-slate-100">{children}</div>}
    </motion.section>
  );
}

export default function GestaoHome() {
  const { allowed = [] } = useOutletContext() || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = getUser();
  const navigate = useNavigate();
  const nome = (user?.name || user?.email || 'Equipe').split(' ')[0];

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/portal-gestao/dashboard');
        setData(res.data);
      } catch (_) { setData(null); } finally { setLoading(false); }
    })();
  }, []);

  const c = data?.cards || {};
  const rotasLiberadas = new Set(allowed.map((m) => m.route.split('?')[0]));
  const atalhos = ATALHOS.filter((a) => rotasLiberadas.has(a.to.split('?')[0]));

  return (
    <div className="mx-auto flex max-w-[1280px] flex-col gap-6" data-testid="gestao-home">
      <motion.div {...fade(0)}>
        <PageHeader variant="hero" kicker="Gestão DNA" title={`Olá, ${nome}`} testid="gestao-header"
          subtitle="Alunos, responsáveis, notas, documentos e rotina escolar em um só lugar." />
      </motion.div>

      {atalhos.length > 0 && (
        <motion.div {...fade(1)} className="flex flex-wrap gap-2.5" data-testid="gestao-atalhos">
          {atalhos.map((a) => (
            <button key={a.key} onClick={() => navigate(a.to)}
              className="group inline-flex items-center gap-2 rounded-full border border-[#EDE7F3] bg-white px-4 py-2.5 text-sm font-semibold text-[#3F1F5C] transition-[background-color,border-color,color] duration-200 hover:border-[#DCC6E8] hover:bg-[#F7F1FA]"
              data-testid={`gestao-atalho-${a.key}`}>
              <a.icon className="h-4 w-4 text-[#7B3F98]" />
              {a.label}
              <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
            </button>
          ))}
        </motion.div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="gestao-home-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando indicadores...
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="gestao-metrics">
          <Metric i={0} icon={GraduationCap} label="Alunos ativos" value={c.alunos_ativos ?? 0}
            hint="matriculados no momento" tone="violet" />
          <Metric i={1} icon={Cake} label="Aniversariantes" value={c.aniversariantes_hoje ?? 0}
            hint={`${c.aniversariantes_mes ?? 0} neste mês`} tone="rose" />
          <Metric i={2} icon={FileSignature} label="Documentos pendentes" value={c.documentos_pendentes ?? 0}
            hint="aguardando análise" tone="sky" />
          <Metric i={3} icon={LifeBuoy} label="Em recuperação" value={c.recuperacoes ?? 0}
            hint="alunos no período atual" tone="amber" />
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <Painel title="Próximos eventos" icon={CalendarDays} items={data?.eventos}
          empty="Nenhum evento futuro no calendário" testId="gestao-painel-eventos"
          action={<Link to="/gestao/calendario" className="text-xs font-bold text-violet-600 hover:text-violet-800">ver calendário</Link>}>
          {(data?.eventos || []).map((e) => (
            <div key={e.id} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-800">{e.titulo || 'Evento'}</div>
                <div className="text-xs text-slate-400">{e.categoria || e.tipo || 'Agenda escolar'}</div>
              </div>
              <span className="whitespace-nowrap rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
                {(e.data_inicio || '').split('-').reverse().join('/')}
              </span>
            </div>
          ))}
        </Painel>

        <Painel title="Alunos em recuperação" icon={LifeBuoy} items={data?.recuperacoes}
          empty="Nenhum aluno em recuperação no período" testId="gestao-painel-recuperacao"
          action={<Link to="/gestao/recuperacao" className="text-xs font-bold text-violet-600 hover:text-violet-800">abrir painel</Link>}>
          {(data?.recuperacoes || []).map((a) => (
            <button key={a.id} onClick={() => navigate(`/gestao/alunos/${a.id}`)}
              className="flex w-full items-center justify-between gap-3 py-3 text-left transition-colors hover:bg-violet-50/50"
              data-testid={`gestao-rec-${a.id}`}>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-800">{a.name}</div>
                <div className="truncate text-xs text-slate-400">{(a.disciplinas || []).join(' · ') || a.class_name || '—'}</div>
              </div>
              <ArrowUpRight className="h-4 w-4 shrink-0 text-slate-300" />
            </button>
          ))}
        </Painel>

        <Painel title="Documentos pendentes" icon={FileSignature} items={data?.documentos}
          empty="Nenhum documento pendente" testId="gestao-painel-documentos"
          action={<Link to="/gestao/documentos" className="text-xs font-bold text-violet-600 hover:text-violet-800">ver documentos</Link>}>
          {(data?.documentos || []).map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-800">{d.label || d.document_type}</div>
                <div className="truncate text-xs text-slate-400">{d.student_name || '—'} · {d.academic_year || ''}</div>
              </div>
              <span className="whitespace-nowrap rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">
                {(d.status || '').replace('_', ' ')}
              </span>
            </div>
          ))}
        </Painel>

        <Painel title="Aniversariantes de hoje" icon={Cake} items={data?.aniversariantes}
          empty="Ninguém faz aniversário hoje" testId="gestao-painel-aniversariantes">
          {(data?.aniversariantes || []).map((a) => (
            <button key={a.id} onClick={() => navigate(`/gestao/alunos/${a.id}`)}
              className="flex w-full items-center gap-3 py-3 text-left transition-colors hover:bg-violet-50/50"
              data-testid={`gestao-birthday-${a.id}`}>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-100 text-xs font-bold text-rose-700">
                {(a.name || '?').slice(0, 2).toUpperCase()}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-slate-800">{a.name}</span>
                <span className="block truncate text-xs text-slate-400">{a.class_name || 'turma não informada'}</span>
              </span>
            </button>
          ))}
        </Painel>
      </div>

      {(data?.alertas || []).length > 0 && (
        <Painel title="Alertas pedagógicos recentes" icon={TriangleAlert} items={data?.alertas}
          empty="Sem alertas" testId="gestao-painel-alertas">
          {(data?.alertas || []).map((a, i) => (
            <div key={i} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0 text-sm text-slate-700">{a.alert_reason}</div>
              <span className="whitespace-nowrap text-xs text-slate-400">{(a.at || '').slice(0, 10)}</span>
            </div>
          ))}
        </Painel>
      )}

      <motion.div {...fade(2)} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="gestao-modules">
        {allowed.map((m, i) => {
          const t = TONES[m.tone] || TONES.violet;
          return (
            <Link key={m.key} to={m.route}
              className="group relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-6 ring-2 ring-transparent transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-28px_rgba(76,29,149,.45)]"
              data-testid={`gestao-card-${m.key}`}>
              <div className={`mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${t.grad} shadow-lg shadow-violet-900/10`}>
                <m.icon className="h-6 w-6 text-white" />
              </div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold tracking-tight text-slate-900">{m.label}</h3>
                {m.readOnly && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-500">leitura</span>}
              </div>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{m.description}</p>
              <ArrowUpRight className="absolute right-5 top-6 h-4 w-4 text-slate-300 transition-all duration-300 group-hover:right-4 group-hover:text-violet-500" />
            </Link>
          );
        })}
      </motion.div>
    </div>
  );
}
